import React, { useEffect, useState, useRef } from "react"
import Api from "../../../../_helpers/Api"
import Skeleton from "react-loading-skeleton"
import { trans } from "../../../../_providers/Translation"
import "../../../../assets/css/partials/status-diagram.css"
import { PieChart } from "@mui/x-charts/PieChart"
import { useCalculateColor } from "@/_helpers/useCalculateColor"
import StarIcon from "@mui/icons-material/Star"
import { Button, Rating } from "@mui/material"
import CompactFilterLayout from "@/components/partials/CompactFilterLayout"
import DateRange from "@/components/partials/DateRange"
import filterImage from "@/assets/img/icons/filter.svg"

const INITIAL_FILTERS = { date_from: null, date_to: null }

export default function ReviewsByCategories() {
  const [state, setState] = useState({
    loading: true,
    data: null,
    averageRate: 0,
  })
  const [filters, setFilters] = useState(INITIAL_FILTERS)

  const compactFilterLayoutRef = useRef(null)
  const fetchDebounceRef = useRef(null)

  const { getColorVariation, gradientId, gradientUrl } = useCalculateColor("reviews-gradient")

  useEffect(() => {
    clearTimeout(fetchDebounceRef.current)
    fetchDebounceRef.current = setTimeout(() => {
      loadData()
    }, 300)
    return () => clearTimeout(fetchDebounceRef.current)
  }, [filters])

  const loadData = () => {
    setState((prev) => ({ ...prev, loading: true }))

    const f = `${filters.date_from ? `?date_from=${filters.date_from || ""}` : ""}${filters.date_to ? `&date_to=${filters.date_to || ""}` : ""}`
    Api.get(`analytics/reviews-by-category${f}`)
      .then((res) => {
        setState({
          loading: false,
          data: res.data.chart_data || null,
          averageRate: res.data.overall_average_rate || 0,
        })
      })
      .catch(() => setState({ loading: false, data: null }))
  }

  function handleFilterChange(name, value) {
    setFilters((prev) => ({ ...prev, [name]: value }))
  }

  function getAppliedFiltersCount() {
    let count = 0
    if (filters.date_from || filters.date_to) count++
    return count
  }

  // Support new API format
  const apiData = state.data || {}
  const labelsArr = Array.isArray(apiData.labels) ? apiData.labels : []
  const repairsArr = Array.isArray(apiData.rates) ? apiData.rates : []
  const revenuesArr = Array.isArray(apiData.reviews) ? apiData.reviews : []

  // Build itemsSource from arrays
  const itemsSource = labelsArr.map((label, idx) => ({
    key: label,
    label,
    revenue: revenuesArr[idx] || 0,
    repairs: repairsArr[idx] || 0,
  }))

  const items = itemsSource
  const total = items.reduce((s, i) => s + Number(i.revenue || 0), 0) || 1

  const pieData = items.map((it, idx) => ({
    id: idx,
    value: Number(it.revenue || 0),
    label: it.label,
    color: gradientUrl(idx),
  }))

  // For each slice: color1 is the base color (used for the legend), color2 is a
  // slightly lighter shade of the same palette — both feed the gradient.
  function getSliceColors(index) {
    return {
      color1: getColorVariation(index),
      color2: getColorVariation(index + 3),
    }
  }

  // Restore the base color on the tooltip swatch (a gradient url() can't be
  // used as a background-color, so the default marker would render empty).
  function PieTooltipContent({ itemData, series }) {
    const point = series.data[itemData.dataIndex]
    const baseColor = getColorVariation(itemData.dataIndex)
    const value = Number(point?.value || 0)
    const percentage = total ? (value / total) * 100 : 0
    return (
      <div className="status-pie-tooltip" style={{
        backgroundColor: "#1E1B39",
        borderRadius: "10px"
      }}>
        <span className="marker" style={{ backgroundColor: baseColor }} />
        <span className="label">{point?.label}</span>
        <span className="value" style={{ color: "#fff" }}>{`${value} ${trans('random.countShort')} (${Math.round(percentage)}%)`}</span>
      </div>
    )
  }

  function getTotalResults() {
    return items.reduce((sum, it) => sum + Number(it.revenue || 0), 0)
  }

  return (
    <div className="reviews-by-categories-chart">
      <div className="head">
        <h3>{trans("analytics.reviewsByCategory")}</h3>
        <div className="right">
          <Button onClick={(e) => { compactFilterLayoutRef.current.open(e) }}>
            <img src={filterImage} alt="Filter" />
            {getAppliedFiltersCount() ? <i>{getAppliedFiltersCount()}</i> : <></>}
          </Button>
          <CompactFilterLayout
            ref={compactFilterLayoutRef}
            appliedFiltersCount={getAppliedFiltersCount()}
            onClearClick={() => setFilters(INITIAL_FILTERS)}
          >
            <div className='row'><div className='col'>
              <DateRange
                startDate={filters.date_from || ''}
                endDate={filters.date_to || ''}
                onStartDateChange={(date) => handleFilterChange('date_from', date)}
                onEndDateChange={(date) => handleFilterChange('date_to', date)}
              />
            </div></div>
          </CompactFilterLayout>
        </div>
      </div>
      <hr />
      <div className="body">
        {state.loading ? (
          <>
            <div className="left-loading">
              <Skeleton height={200} count={1} />
            </div>
            <div className="right-loading">
              <Skeleton height={50} count={4} />
            </div>
          </>
        ) : getTotalResults() === 0 ? (
          <div
            className="alert warning"
            style={{
              marginBottom: 0,
              width: "100%",
            }}
          >
            {trans("messages.noDataFound")}
          </div>
        ) : (
          <>
            <div className="left">
              <PieChart
                series={[
                  {
                    data: pieData,
                    valueFormatter: (item) => {
                      const percentage = total ? (item.value / total) * 100 : 0
                      return `${item.value} ${trans('random.countShort')} (${Math.round(percentage)}%)`
                    },
                  },
                ]}
                sx={{
                  "& .MuiPieArc-root": {
                    stroke: "none",
                    strokeWidth: 0,
                  },
                }}
                width={200}
                height={200}
                margin={{ right: 0 }}
                slotProps={{
                  legend: { hidden: true },
                }}
                slots={{
                  itemContent: PieTooltipContent,
                }}
              >
                <defs>
                  {pieData.map((_, idx) => {
                    const { color1, color2 } = getSliceColors(idx)
                    return (
                      <linearGradient
                        key={idx}
                        id={gradientId(idx)}
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop offset="0%" stopColor={color1} />
                        <stop offset="100%" stopColor={color2} />
                      </linearGradient>
                    )
                  })}
                </defs>
              </PieChart>
              <p>{trans("analytics.overallRating")}</p>
              <b>{state.averageRate}</b>
              <Rating
                name="text-feedback"
                value={state?.averageRate || 0}
                readOnly
                precision={0.1}
                emptyIcon={<StarIcon style={{ color: "#DBDBDB" }} />}
              />
            </div>

            <div className="right">
              <table>
                <tbody>
                  {items.map((it, idx) => {
                    const color = getColorVariation(idx)
                    return (
                      <tr key={it.key || it.label || idx}>
                        <td>
                          <span style={{ background: color }} />
                        </td>
                        <td>
                          <b>{it.label || it.name}</b>
                        </td>
                        <td>
                          <div
                            className="count"
                            style={{
                              color: "#8F7100",
                              fontFamily: "Inter Bold",
                            }}
                          >
                            {it.repairs}
                            <StarIcon />
                          </div>
                        </td>
                        <td>
                          <div className="count">{`(${it.revenue} ${trans('random.countShort')})`}</div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
